import styled from "styled-components";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { mobileMarginVal } from "../../styleConstants";

const Wrapper = styled.div`
  padding-top: ${(props) => (props.$useMobileMargins ? "16px" : "54px")};
  padding-bottom: ${(props) => (props.$useMobileMargins ? "28px" : "54px")};

  // just eye-balled what is a good page width.
  // for mobile views, I want to set a max width to the screen width minus mobile margins.
  // This is to keep things within margins even when a slide in is open.
  max-width: min(680px, calc(100vw - ${mobileMarginVal * 2}px));
  // max-width: 70ch;
`;

const Title = styled.div`
  padding-top: 4px;
  font-size: ${(props) => (props.$useMobileMargins ? "22px" : "22px")};
  font-weight: 500;
  padding-bottom: 4px;
`;

const LastUpdateMessage = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.64);
`;

const Emphasized = styled.span`
  font-weight: 500;
  color: black;
`;

const Bo = styled.span`
  font-weight: 600;
  color: black;
`;

const SubTitle = styled.div`
  margin-top: 48px;
  font-size: 20px;
  font-weight: 500;
  padding-bottom: 20px;
`;

const TitleSmall = styled.div`
  padding-top: 48px;
  font-size: 20px;
  font-weight: 500;
  margin-bottom: -8px;
`;

const CuA = styled.a`
  color: black;
`;

const CuAB = styled.a`
  color: black;
  font-weight: 500;
`;

const CuSec = styled.section`
  scroll-margin-top: 80px;
`;

const dateLastUpdated = "December 12, 2025";

const PrivacyPolicy = () => {
  const screenWidth = useSelector((state) => state.screenWidth.value);

  // matches with home and petition. slightly smaller cutoff than results
  const useMobileMargins = screenWidth < 540;

  return (
    <>
      <Wrapper $useMobileMargins={useMobileMargins}>
        <Title $useMobileMargins={useMobileMargins}>Privacy Policy</Title>
        <LastUpdateMessage>
          Last updated: <Emphasized>{dateLastUpdated}</Emphasized>
        </LastUpdateMessage>
        <TitleSmall>Table of Contents</TitleSmall>
        <ol>
          <li>
            <CuA href="#s1">Introduction</CuA>
          </li>
          <li>
            <CuA href="#s2">Voter Information Obtained from States</CuA>
          </li>
          <li>
            <CuA href="#s3">Information Collected on Verified Voice</CuA>
          </li>
          <li>
            <CuA href="#s4">How Information is Used</CuA>
          </li>
          <li>
            <CuA href="#s5">How Information is Shared</CuA>
          </li>
          <li>
            <CuA href="#s6">How Information is Stored</CuA>
          </li>
          <li>
            <CuA href="#s7">Data Retention</CuA>
          </li>
          <li>
            <CuA href="#s8">User Rights</CuA>
          </li>
          <li>
            <CuA href="#s9">Cookies and Browser Persistence</CuA>
          </li>
          <li>
            <CuA href="#s10">International Access</CuA>
          </li>
          <li>
            <CuA href="#s11">Links to Other Websites</CuA>
          </li>
          <li>
            <CuA href="#s12">Policy Updates</CuA>
          </li>
          <li>
            <CuA href="#s13">Contact Information</CuA>
          </li>
        </ol>
        <CuSec id="s1">
          <SubTitle>1. Introduction</SubTitle>
          At Verified Voice, your privacy is important to us. This Privacy
          Policy explains how we collect, use, store, and share your personal
          information when you use our platform. Verified Voice provides a
          secure environment to advocate for or against policy ideas.
          <br />
          <br />
          By using Verified Voice, you agree to the collection and use of your
          personal information in accordance with this Privacy Policy. If you
          have any questions or concerns, feel free to reach out to us at{" "}
          <Emphasized>info@verifiedvoice.org</Emphasized>. Your communications
          with us will be retained and used as detailed in this Privacy Policy.
          Refer to the <CuAB href="#s13">Contact Information</CuAB> section for
          more info.
        </CuSec>
        <CuSec id="s2">
          <SubTitle>2. Voter Information Obtained from States</SubTitle>
          Verified Voice legally obtains voter lists from the state of Florida.
          These lists include information about registered voters and are
          provided to us under state regulations.
          <br />
          <br />
          <Bo>For Florida:</Bo>
          <br />
          Read about how we obtain Florida voter data{" "}
          <CuAB
            href="https://dos.fl.gov/elections/data-statistics/voter-registration-statistics/voter-extract-request/"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </CuAB>
          . Florida voter data is generally public, although some voter data is
          kept confidential and not made available to us. You can read more
          about how{" "}
          <CuAB
            href="https://dos.fl.gov/elections/for-voters/voter-registration/voter-information-as-a-public-record/"
            target="_blank"
            rel="noopener noreferrer"
          >
            voter data is public
          </CuAB>{" "}
          in Florida. Note that confidential voter records still include the{" "}
          {"voter's"} county code, voter ID, public records exemption, gender,
          race, registration date, political party, and voter status, while
          excluding personal information like name, address, and contact
          information. The public, non-confidential voter information that is
          given to us typically includes:
          <ul>
            <li>County code and voter ID</li>
            <li>Full name (first, middle, last, and suffix)</li>
            <li>Full address and mailing address (including zip code)</li>
            <li>Phone number and email address</li>
            <li>Date of birth, gender, race, and political party</li>
            <li>
              Voter status ({`"Active"`} or {`"Inactive"`}) and registration
              date
            </li>
            <li>
              Precinct and various district assignments (congressional, state
              senate, state house, county commission, and school board)
            </li>
            <li>
              Public Records Exemption ({`"Y"`} if voter record is protected and
              not included, {`"N"`} if the voter record is publicly available)
            </li>
          </ul>
        </CuSec>
        <CuSec id="s3">
          <SubTitle>3. Information Collected on Verified Voice</SubTitle>
          In addition to obtaining voter data from Florida, Verified Voice
          collects specific information directly from users. This includes:
          <ul>
            <li>
              <Bo>Account and Authentication Information:</Bo> When you sign in,
              we collect the phone number or email address you use for
              authentication. If you contact us to update your account—for
              example, to add alternate phone numbers or email addresses or to
              combine multiple accounts—we will add and store those additional
              contact methods after confirming that you control them.
            </li>
            <li>
              <Bo>Optional Profile and Location Information:</Bo> Once{" "}
              {"you've"} signed in and created an account, we may contact you to
              ask for optional information such as your name, state, city,
              mailing address, and legislative districts (for example, your
              congressional, state senate, or state house/assembly districts).
              This information helps us determine which representatives are
              relevant for your account, guides our outreach to public offices,
              and helps us understand our users.
            </li>
            <li>
              <Bo>Voter Verification Information (Florida Only):</Bo> If you are
              a Florida voter and choose to verify your account, we may ask you
              for information necessary to help locate your voter record (such
              as your name, date of birth, or address). We will also require you
              to confirm control of a phone number or email address that appears
              on that record to link your account to that voter record.
            </li>
            <li>
              <Bo>Petition Stances:</Bo> When you support or oppose a petition,
              we record your stance and associate it with your account and,
              where applicable, with any voter information linked to your
              account. We also record the timestamp of each stance and maintain
              a full history of your stances for each petition.
            </li>
            <li>
              <Bo>Communication and Petition Submissions:</Bo> When you
              communicate with Verified Voice—whether for petition submissions,
              general inquiries, verification or account requests, or other
              purposes—we collect and retain your contact information (such as
              your email address or phone number) along with the contents of
              your message. We use this information to respond to inquiries,
              provide support, and process petition submissions or
              account-related requests. This is described further in our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              , particularly the <Emphasized>User-Generated Content</Emphasized>{" "}
              section.
            </li>
            <li>
              <Bo>Legislator Stance Submissions:</Bo> If you volunteer to help
              compile legislator stances for a petition, we collect and retain
              the information you submit, which may include the stance
              classification (e.g., Support, Oppose, Unclear), a detailed
              explanation or justification, relevant public sources, the{" "}
              {"legislator's"} name, and the associated petition. This
              information is stored for review and public display.
            </li>
          </ul>
        </CuSec>
        <CuSec id="s4">
          <SubTitle>4. How Information is Used</SubTitle>
          Verified Voice uses state voter data and the information collected on
          the platform to provide a secure and functional experience. {
            "Here's"
          }{" "}
          how we use the data:
          <ul>
            <li>
              <Bo>Authentication and Accounts:</Bo> When you sign in with your
              phone number or email, we use this information to create and
              maintain your Verified Voice account, keep you signed in, and
              associate your activity (such as petition stances) with your
              account. If you ask us to add alternate phone numbers or emails or
              to combine multiple accounts, we store this information to allow
              you to sign in with any of your added contacts and, where
              applicable, to determine whether your contact information matches
              any voter record you choose to verify.
            </li>
            <li>
              <Bo>Optional Voter Verification (Florida Only):</Bo> If you are a
              Florida voter and choose to link your account to your voter record
              on file, we can do this by matching your confirmed phone number or
              email address with a Florida voter record we have on file. Once
              linked, you can act as a verified voter on the platform and view
              certain voter-related information associated with that record.
              Your petition stances will be associated with your verified voter
              information.
            </li>
            <li>
              <Bo>Use of Public Florida Voter Information for Inquiries:</Bo>{" "}
              Because Florida voter information is generally public by law,
              anyone may contact us to inquire about Florida voter information
              we have on file, such as registration status or the contact
              information on file for a given voter record. This can be helpful
              if someone is not sure about their voter registration status or
              contact information on file but wants to verify their account or
              update their registration. When we respond to these inquiries, we
              rely solely on the public voter information provided to us by the
              state and may, at our discretion, limit the level of detail we
              share (for example, by masking parts of an email address or phone
              number). This inquiry process is separate from how voter
              information is accessed through your signed-in account on the
              Verified Voice platform.
            </li>
            <li>
              <Bo>Profile and Representative Information:</Bo> For verified
              Florida voters, we may display voter-related information
              associated with your linked voter record—such as your full name,
              city, and district assignments (for example, city council, state
              house/assembly, state senate, and congressional districts)—so that
              you can see which voter you are acting as. Likewise, any profile
              information you provide to us may also be displayed to you on the
              platform. Your voter information, as well as profile or location
              information (such as your state, address, or legislative
              districts) you provide directly to us, may be used to determine
              which elected officials represent you, to configure your Voter
              Guide view, to help guide our outreach to public offices, and to
              generally understand our user base.
            </li>
            <li>
              <Bo>Petition Support/Opposition:</Bo> When you support or oppose a
              petition, your stance is recorded and linked to your account and,
              where applicable, to any submitted profile information or voter
              information linked to your account. We aggregate these stances
              with others to display the overall support/opposition of petitions
              on the platform. Aggregated results may be broken down by
              geography (for example, city council district, city, county, state
              senate district, state house/assembly district, or congressional
              district) or by voter demographics such as age, gender, political
              party, race, language, or voter status when such information is
              available from voter records. Aggregated results never include
              personal identifiers.
            </li>
            <li>
              <Bo>Legislator Stance Compilation:</Bo> When a user, petition
              author, or volunteer submits legislator stance information, we use
              that data to display stances of relevant legislators on individual
              petition pages and within the Voter Guide. These stances are
              reviewed by Verified Voice and may be edited or removed for
              clarity, accuracy, or relevance. We retain submitted stance
              information to maintain the integrity and continuity of public
              petition records.
            </li>
            <li>
              <Bo>Platform Security and Integrity:</Bo> We may use your
              information internally to safeguard the platform, such as
              verifying user/voter authenticity, monitoring for suspicious
              activity, and preventing unauthorized access, to ensure the
              security and functionality of Verified Voice.
            </li>
            <li>
              <Bo>Communication:</Bo> We may use your phone number or email
              address to communicate with you for customer support or to provide
              updates regarding our platform, petitions, policies, or Terms, as
              detailed by our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              . We also rely on the contents of your messages to respond to
              inquiries and provide effective support.
            </li>
            <li>
              <Bo>Petition Submissions:</Bo> We review and evaluate petition
              submissions sent to us by email. If a petition is approved, it may
              be publicly displayed on Verified Voice. By submitting a petition,
              you consent to its public display and retention. Requests for
              edits or removal will be considered in good faith but remain at
              our discretion.
            </li>
            <li>
              <Bo>Petition Submission Retention:</Bo> Approved petitions may be
              retained indefinitely to ensure the continuity and relevance of
              public policy discussions hosted on Verified Voice.
            </li>
            <li>
              <Bo>Voter Guide Features:</Bo> When you visit the Voter Guide, you
              may view petition stances of a selected legislator compared with
              your own stances.
            </li>
          </ul>
        </CuSec>
        <CuSec id="s5">
          <SubTitle>5. How Information is Shared</SubTitle>
          We limit the sharing of personal information to protect your privacy.{" "}
          {"Here's"} how your information may be shared:
          <ul>
            <li>
              <Bo>Aggregated Data:</Bo> Petition stances are linked to voter
              data and/or profile information, and they are combined to display
              aggregated totals of petition support and opposition on the
              platform. These results may be broken down by geography (e.g.,
              city council district, city, county, state senate district, state
              assembly/house district, or congressional district) or voter
              demographics such as age, gender, political party, race, language,
              voter status, or other characteristics provided in the voter data.
              We may also combine multiple demographics (e.g., gender and
              political party) for a more detailed breakdown. By retaining
              timestamps and historical stance data, we can generate and share
              aggregated support/opposition results as they are at present or as
              they were at a specific time in the past. This aggregated data is
              publicly visible but does not include personal identifiers, such
              as names or contact details.
            </li>
            <li>
              <Bo>Legislator Stance Display:</Bo> Legislator stances submitted
              by Verified Voice, petition authors, or volunteers are publicly
              displayed alongside petitions and in the Voter Guide. These are
              not linked to personal user data.
            </li>
            <li>
              <Bo>Sharing Aggregated Data with Third Parties:</Bo> In addition
              to displaying aggregated petition support and opposition results
              on the platform, we may share these same results with political
              campaigns or candidates, government offices, elected officials,
              research organizations, or other individuals or organizations. The
              data shared will be in the same aggregated and anonymized format,
              without any personal identifiers such as names or contact
              information.
            </li>
            <li>
              <Bo>Sharing Voter Information with Verified Users:</Bo> When your
              account has been linked to a voter record (for example, through
              optional Florida voter verification), you may view information
              associated with that voter record, such as the {"voter's"} full
              name, city, district assignments, contact information, and other
              fields contained in the public voter file, while you are signed in
              to your account on the Verified Voice platform.
            </li>
            <li>
              <Bo>Sharing Florida Voter Information:</Bo> Because Florida voter
              information is generally public by law, anyone may contact us to
              inquire about Florida voter information we have on file, such as
              the registration status or the contact information on file for a
              given voter record. We may share this information to help people
              understand their registration status or how to verify their
              account. When we respond to these inquiries, we rely solely on the
              public voter information provided to us by the state and may, at
              our discretion, limit the level of detail we share (for example,
              by masking parts of an email address or phone number).
            </li>
            <li>
              <Bo>Third Parties:</Bo> We do not share your personal information,
              including your phone number, email address, voter information, or
              specific petition stances, with third parties for marketing or
              commercial purposes. User information is stored in Google Cloud,
              an industry-standard third-party service used to host our
              platform. This is outlined in the{" "}
              <CuAB href="#s6">How Information is Stored</CuAB> section.
            </li>
            <li>
              <Bo>Fraud Prevention and Legal Compliance:</Bo> We may disclose
              user information to third parties, such as law enforcement or
              investigators, to comply with legal obligations, respond to valid
              legal requests (e.g., law enforcement inquiries or court orders),
              or investigate and prevent fraudulent activities, unauthorized
              access, or security threats that could harm Verified Voice, its
              users, or the public.
            </li>
          </ul>
        </CuSec>
        <CuSec id="s6">
          <SubTitle>6. How Information is Stored</SubTitle>
          Verified Voice is hosted on the Google Cloud Platform (GCP), a secure,
          industry-standard provider for web applications and cloud data
          storage. We use services like Google Cloud Storage, BigQuery,
          Firebase, and Firestore to store and manage voter and user data.{" "}
          {"Here's"} how we handle your information:
          <ul>
            <li>
              <Bo>Florida Voter Lists:</Bo> Voter lists from Florida are sent by
              physical CD. The data is processed, cleaned, and stored locally
              before being uploaded to Google Cloud Storage and Google BigQuery.
              Verified Voice queries these voter lists in BigQuery as needed,
              such as for voter verification or retrieving voter status and
              contact information.
            </li>
            <li>
              <Bo>BigQuery Logs:</Bo> Every query made to the BigQuery voter
              list is logged internally. This includes searches for
              authenticated phone numbers or emails during voter verification,
              or for a specific {"voter's"} status and contact information. The
              log records what was searched (e.g., phone number, email, or first
              and last name, date of birth, and zip code) and the time of the
              query. Logs are retained for 6 months.
            </li>
            <li>
              <Bo>Authenticated Users:</Bo> Phone numbers and email addresses
              used for authentication are managed and stored securely through
              Firebase Auth. Firebase Auth also logs the first and most recent
              sign-in for all users.
            </li>
            <li>
              <Bo>Voter Profile Information and Petition Stances:</Bo> Any
              profile information a user submits to us is stored with their
              account information in Firestore. When a {"user's"} account is
              linked to a voter from the voter list (for example, after optional
              Florida voter verification), the {"voter's"} information—including
              full name, address, district assignments, contact information,
              demographic fields, and voter status—is copied from the BigQuery
              list into Firestore. A {"user's"} inputted petition stances and
              petition stance histories are also stored in Firestore.
            </li>
            <li>
              <Bo>Data Access:</Bo> The Verified Voice client (user-facing
              browser interface) accesses user and voter data either through
              secure Google Cloud Functions or directly from Firestore. Strict
              security rules ensure that, when using the Verified Voice
              platform, signed-in users can access only the account information
              associated with their authenticated contact information and any
              voter information linked to their account through the verification
              process. Data may also be accessed during internal monitoring or
              to generate reports of aggregated petition stances.
            </li>
            <li>
              <Bo>Communication Records and Petition Submissions:</Bo>{" "}
              Communication records, including petition submissions, general
              inquiries, and support requests, are stored securely in our email
              system (Gmail for email communications) and Google Voice (for
              phone and text communications). Petitions approved for inclusion
              on Verified Voice are reformatted for frontend use and hosted
              publicly on our platform through Firebase Hosting, where they are
              accessible to all site visitors and the general public.
            </li>
            <li>
              <Bo>Legislator Stance Data:</Bo> Legislator stances displayed on
              Verified Voice are currently compiled manually by platform
              administrators or contributors and stored directly in the{" "}
              {"platform's"} codebase. These stances are not submitted or stored
              through the {"platform's"} user interface and are not part of user
              account data. Volunteers may assist with stance research through
              off-platform tools such as shared documents or internal databases.
              Any such processes will continue to be separate from the storage
              of personal user information.
            </li>
          </ul>
        </CuSec>
        <CuSec id="s7">
          <SubTitle>7. Data Retention</SubTitle>
          Verified Voice retains personal information for as long as necessary
          to provide our services, fulfill legal obligations, or maintain
          security and functionality on the platform. Specifically:
          <ul>
            <li>
              <Bo>User-Provided Data and Profile Information:</Bo> Information
              you provide through the platform or when you contact us (such as
              petition stances or profile and location information), as well as
              voter profile information copied from the voter list, may be
              retained as long as it is necessary to maintain platform
              functionality, continuity of service, and accurate petition
              records. While some data may be retained indefinitely to ensure
              these purposes, it may also be deleted if it is no longer required
              or in cases of account inactivity or account termination, as
              detailed below.
            </li>
            <li>
              <Bo>Inactive Accounts:</Bo> If an account remains inactive for a
              prolonged period, Verified Voice reserves the right to take
              actions such as terminating the account or deleting associated
              data to optimize platform resources. Data associated with inactive
              accounts may be retained or deleted in accordance with this policy
              and applicable laws. For more details about accounts and account
              inactivity, refer to the{" "}
              <Emphasized>
                User Accounts, Conduct, and Responsibilities
              </Emphasized>{" "}
              section of our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              .
            </li>
            <li>
              <Bo>Account Termination:</Bo> In addition to actions related to
              account inactivity, Verified Voice may delete or retain data
              associated with accounts terminated due to violations of our Terms
              of Service. For more details about account termination, please
              refer to the{" "}
              <Emphasized>Monitoring, Enforcement, and Termination</Emphasized>{" "}
              section of our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              .
            </li>
            <li>
              <Bo>Voter Data:</Bo> The voter list from Florida is legally
              obtained and updated periodically as provided by the state.
              Verified Voice retains this data as long as necessary to support
              the verification process. Users wishing to update or correct voter
              data should contact {"Florida's"} election authority.
            </li>
            <li>
              <Bo>Authentication Data:</Bo> Verified Voice stores your
              authenticated account information, including authenticated phone
              or email and first and most recent sign-in timestamps, in Firebase{" "}
              {"Auth's"} backend system. This data is retained while the account
              exists, but may be deleted if the account is inactive or upon user
              request.
            </li>
            <li>
              <Bo>BigQuery Logs:</Bo> Queries of our BigQuery voter list (for
              voter verification or to find voter status and contact info on
              file), including timestamp and information searched in the query,
              are retained in internal logs for 6 months.
            </li>
            <li>
              <Bo>Communication Records and Petition Submissions:</Bo> Records
              from communications—including petition submissions, general
              inquiries, and support requests—are retained as long as necessary
              to provide responsive service, address inquiries, or maintain
              platform quality. Approved petition submissions may be retained
              indefinitely to ensure continuity of public policy discussions
              hosted on Verified Voice.
            </li>
            <li>
              <Bo>Legislator Stance Data:</Bo> Because legislator stances are
              compiled manually and stored outside the core user data
              infrastructure, they are retained indefinitely as part of the
              Verified Voice platform content. These stances may be updated or
              removed at Verified {"Voice's"} discretion to reflect new
              information or platform needs.
            </li>
          </ul>
          If you have any questions or would like to request the deletion of any
          personal information you provided, please contact us at{" "}
          <Emphasized>info@verifiedvoice.org</Emphasized>. Your communications
          with us will be retained and used as detailed in this Privacy Policy.
          Refer to the <CuAB href="#s13">Contact Information</CuAB> section for
          more info.
        </CuSec>
        <CuSec id="s8">
          <SubTitle>8. User Rights</SubTitle>
          At Verified Voice, we provide you with the ability to access, correct,
          or delete any of the personal information you provided through our
          platform. This does not apply to the voter data we legally obtain from
          Florida. Here are your rights, compliant with the California Consumer
          Privacy Act (CCPA):
          <ul>
            <li>
              <Bo>Access and Correction:</Bo> You have the right to request
              access to any personal information collected by our platform, such
              as your petition stances, and to request corrections to any
              inaccurate information you provided. To access or correct your
              data, contact us at{" "}
              <Emphasized>info@verifiedvoice.org</Emphasized>. Please note that
              corrections to voter registration information must be made through
              your {"state's"} election authority.
            </li>
            <li>
              <Bo>Deletion:</Bo> You may request the deletion of your account
              and personal information provided through Verified Voice, such as
              your petition stances. However, this does not apply to voter data
              that we receive from the state. Deleting your account data will
              remove your access to the platform, but your voter information
              will remain in our voter lists. To request account deletion,
              contact us at <Emphasized>info@verifiedvoice.org</Emphasized>. To
              request data deletion, users must provide sufficient information
              to verify their identity, such as a valid email or phone number
              associated with their account. Verified Voice reserves the right
              to delete data associated with inactive or terminated accounts in
              accordance with this Privacy Policy.
            </li>
            <li>
              <Bo>Communication Opt-Out:</Bo> You can opt out of receiving
              non-essential communications from us by contacting{" "}
              <Emphasized>info@verifiedvoice.org</Emphasized>. Note that opting
              out of non-essential communications will not affect your receipt
              of essential communications, which include updates regarding
              account security, terms, or policies. This is further outlined in
              our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              .
            </li>
            <li>
              <Bo>Sale of Personal Data:</Bo> Verified Voice does not sell
              personal information.
            </li>
            <li>
              <Bo>Discrimination:</Bo> You have the right to not be
              discriminated against for exercising your privacy rights.
            </li>
          </ul>
          If you have any questions or wish to exercise your rights, please
          contact us at <Emphasized>info@verifiedvoice.org</Emphasized>. Your
          communications with us will be retained and used as detailed in this
          Privacy Policy. Refer to the{" "}
          <CuAB href="#s13">Contact Information</CuAB> section for more info. To
          access, modify, or delete your official voter data, please contact
          your {"state's"} election authority.
        </CuSec>
        <CuSec id="s9">
          <SubTitle>9. Cookies and Browser Persistence</SubTitle>
          Verified Voice uses browser persistence mechanisms to ensure smooth
          user experiences and session continuity while you are signed in.
          <br />
          <br />
          While you are signed in with your phone number or email, your
          authenticated session, along with the contact information you use for
          sign-in, is stored in the browser using Firebase Auth. Additionally,
          any profile or voter-related information associated with your
          account—such as your name, city, district assignments, state, and
          petition stances (including timestamps and stance history)—may remain
          in the browser until you log out. To manage browser storage, we use
          Redux Persist, a tool that works with our front-end framework, React.
          <br />
          <br />
          This browser-based persistence is used solely to enhance platform
          functionality, and no data is shared or tracked for advertising
          purposes. You can manage browser-stored data by logging out or
          clearing your browser cache.
        </CuSec>
        <CuSec id="s10">
          <SubTitle>10. International Access</SubTitle>
          Verified Voice is primarily intended for users in the United States.
          If you access Verified Voice from outside the United States, you do so
          at your own risk and acknowledge that your data will be stored and
          processed in the U.S. By using Verified Voice, you consent to the
          transfer, storage, and processing of your data in accordance with this
          Privacy Policy and U.S. laws, which may differ from the laws in your
          country. You can read more about this in the{" "}
          <Emphasized>International Use</Emphasized> section of our{" "}
          <Link
            to="/about/terms-of-service"
            target="_blank"
            style={{ color: "black", fontWeight: "500" }}
          >
            Terms of Service
          </Link>
          .
        </CuSec>
        <CuSec id="s11">
          <SubTitle>11. Links to Other Websites</SubTitle>
          Verified Voice may contain links to external websites for
          informational purposes or for ease of access to related resources.
          Please be aware that once you leave Verified Voice, this Privacy
          Policy does not apply to external sites. Verified Voice does not
          control and is not responsible for the content, privacy practices, or
          policies of these websites. We encourage you to review the privacy
          policies of any third-party sites you visit. You can read more about
          this in the{" "}
          <Emphasized>
            Links to External Sites and Third-Party Services
          </Emphasized>{" "}
          section of our{" "}
          <Link
            to="/about/terms-of-service"
            target="_blank"
            style={{ color: "black", fontWeight: "500" }}
          >
            Terms of Service
          </Link>
          .
        </CuSec>
        <CuSec id="s12">
          <SubTitle>12. Policy Updates</SubTitle>
          We may update this Privacy Policy from time to time to reflect changes
          in our practices, legal requirements, or other factors. When we make
          changes, we will post the revised policy on this page and update the
          “Last Updated” date at the top. The most recent version of this
          Privacy Policy is always available here at{" "}
          <Link to="/about/privacy-policy" style={{ color: "black" }}>
            verifiedvoice.org/about/privacy-policy
          </Link>
          . If the changes are significant, we may also notify you through email
          or text.
          <br />
          <br />
          By continuing to use Verified Voice after any updates to this policy,
          you agree to the terms of the revised Privacy Policy. It is your
          responsibility to periodically review this Privacy Policy to stay
          informed of updates.
        </CuSec>
        <CuSec id="s13">
          <SubTitle>13. Contact Information</SubTitle>
          If you have any questions or concerns about Verified Voice, this
          Privacy Policy, or our Terms of Service, or you wish to exercise your
          rights related to your personal information, please contact us at:
          <br />
          <br />
          <Bo>info@verifiedvoice.org</Bo>
          <br />
          <br />
          This email address serves as {"Verified Voice's"} official channel for
          receiving all communications, including general inquiries, feedback or
          suggestions, and requests related to your personal information and
          user rights.
          <br />
          <br />
          By contacting Verified Voice, you acknowledge and agree that:
          <ul>
            <li>
              Your communication may be retained in accordance with this Privacy
              Policy to address your query, improve our platform, or fulfill
              legal or operational requirements.
            </li>
            <li>
              Feedback submissions, including suggestions or ideas for improving
              the platform, as well as petition submissions, are considered
              voluntary contributions and are processed in line with the{" "}
              <Emphasized>User-Generated Content</Emphasized> section of our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "black", fontWeight: "500" }}
              >
                Terms of Service
              </Link>
              .
            </li>
          </ul>
          Please ensure your communication includes all relevant details to
          facilitate prompt review and response.
        </CuSec>
      </Wrapper>
    </>
  );
};

export default PrivacyPolicy;
